// Cookie / tracking consent.
//
// EU/EEA, UK and Switzerland visitors are asked first (Oct 19, Felipe): the
// Worker sets msth_region=ask on their pages, and analytics (Google
// Analytics, LogRocket, our own first-party analytics) stay off until they
// accept. Everyone else keeps "notice + opt-out": analytics run until they
// decline via the cookie banner, the "Cookie preferences" footer link, or the
// CCPA "Do Not Sell or Share My Personal Information" link. The choice is
// stored in localStorage and honored by index.html (the GA disable flag) and
// src/main.tsx (the LogRocket init gate).

/** The GA4 property of the market being viewed (index.html sets window.__GA_ID from the host); null off the real domains. */
export const gaMeasurementId = (): string | null =>
  typeof window === "undefined" ? null : ((window as unknown as { __GA_ID?: string | null }).__GA_ID ?? null);
export const COOKIE_CONSENT_KEY = "msth_cookie_consent_v1";
export const COOKIE_CONSENT_VERSION = 1;
/** Set by the Worker on every page: "ask" (EU/EEA, UK, CH) or "notice". */
export const CONSENT_REGION_COOKIE = "msth_region";

/** Whether this visitor must accept before any analytics runs. */
export function askFirstRegion(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim() === `${CONSENT_REGION_COOKIE}=ask`);
}

const CHANGED_EVENT = "msth-cookie-consent-changed";
const OPEN_PREFS_EVENT = "msth-open-cookie-preferences";

export interface CookieConsent {
  analytics: boolean;
  version: number;
  updatedAt: string;
}

export function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (typeof parsed?.analytics !== "boolean") return null;
    return {
      analytics: parsed.analytics,
      version: typeof parsed.version === "number" ? parsed.version : COOKIE_CONSENT_VERSION,
      updatedAt: parsed.updatedAt ?? "",
    };
  } catch {
    return null;
  }
}

/** Whether the visitor has made an explicit choice yet (banner gating). */
export function hasDecision(): boolean {
  return getStoredConsent() !== null;
}

/** A stored choice wins; otherwise ask-first regions start off, the rest on. */
export function hasAnalyticsConsent(): boolean {
  const c = getStoredConsent();
  return c ? c.analytics : !askFirstRegion();
}

/**
 * Apply the Google Analytics opt-out global based on the stored preference.
 * `window['ga-disable-<ID>'] = true` is GA's official kill switch and is read
 * by gtag.js on every hit. Safe to call repeatedly.
 */
export function applyAnalyticsPreference(): void {
  if (typeof window === "undefined") return;
  const disabled = !hasAnalyticsConsent();
  const id = gaMeasurementId();
  if (id) (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = disabled;
  if (disabled) clearAnalyticsCookies();
}

function clearAnalyticsCookies(): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = new Set<string>([host, `.${host}`]);
  if (parts.length > 2) {
    const base = parts.slice(-2).join(".");
    domains.add(base);
    domains.add(`.${base}`);
  }
  const names = document.cookie.split(";").map((c) => c.split("=")[0].trim());
  for (const name of names) {
    if (name === "_ga" || name === "_gid" || name.startsWith("_ga_") || name.startsWith("_gat")) {
      for (const d of domains) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${d}`;
      }
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  }
}

export function setCookieConsent(analytics: boolean): void {
  const value: CookieConsent = {
    analytics,
    version: COOKIE_CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* storage disabled, the preference simply won't persist */
  }
  applyAnalyticsPreference();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: value }));
  }
}

/** Reopen the cookie banner (footer "Cookie preferences" link). */
export function openCookiePreferences(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
  }
}

export function onConsentChanged(cb: (c: CookieConsent) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<CookieConsent>).detail);
  window.addEventListener(CHANGED_EVENT, handler);
  return () => window.removeEventListener(CHANGED_EVENT, handler);
}

export function onOpenPreferences(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener(OPEN_PREFS_EVENT, handler);
  return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
}
